import React, { useMemo, useState } from 'react'
import { Box, Skeleton, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import BarChartIcon from '@mui/icons-material/BarChart'
import { Bar, Line } from 'react-chartjs-2'
import { allZero, axes, baseOptions, comparisonBars, comparisonLine, currentBars, currentLine, htmlTooltip, niceScale } from './chartKit'
import { C, cardSx, tabular } from './tokens'
import { count, dayShort, dayWithWeekday, delta, money, pct, rangeLabel } from './format'
import Empty from './Empty'

const METRICS = [
  { key: 'sales', label: 'Sales', money: true },
  { key: 'orders', label: 'Orders', money: false },
  { key: 'aov', label: 'Avg order value', money: true }
]

const valueOf = (metric, sales, orders) => {
  if (sales == null) return null
  if (metric === 'sales') return sales
  if (metric === 'orders') return orders
  return orders ? sales / orders : 0
}

export const cardTitleSx = { fontSize: 16, fontWeight: 600, color: C.text }
export const cardSubSx = { fontSize: 13, color: C.textMuted, mt: 0.25 }

export const tabsSx = {
  minHeight: 36,
  '& .MuiTabs-indicator': { bgcolor: C.brand, height: 2 },
  '& .MuiTab-root': {
    minHeight: 36,
    minWidth: 0,
    px: 1.5,
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: C.textMuted,
    '&.Mui-selected': { color: C.text },
    '&.Mui-focusVisible': { outline: `2px solid ${C.brandText}`, outlineOffset: -2 }
  }
}

/** "▲ $12.40 (25%)" — the coloured change line in every tooltip. */
export function tooltipDelta(cur, prev, fmt) {
  if (cur == null || prev == null) return null
  const diff = Number(cur) - Number(prev)
  if (Math.abs(diff) < 0.005) return { text: 'No change', good: null }
  const d = delta(cur, prev)
  return { text: `${diff > 0 ? '▲' : '▼'} ${fmt(Math.abs(diff))}${d == null ? ' · New' : ` (${pct(d)})`}`, good: diff > 0 }
}

export default function SalesOverTime({ data, loading, compareText, onTry30 }) {
  const [metric, setMetric] = useState('sales')
  const [kind, setKind] = useState('line')
  const m = METRICS.find(x => x.key === metric)
  const hasPrev = !!data?.previous
  const days = data?.days || []
  const fmt = m.money ? money : count

  const series = useMemo(() => ({
    // Closed days are null → a gap in the line, never a fake zero.
    cur: days.map(d => (d.closed ? null : valueOf(metric, d.sales, d.orders))),
    prev: hasPrev ? days.map(d => valueOf(metric, d.prevSales, d.prevOrders)) : []
  }), [days, metric, hasPrev])

  const totals = useMemo(() => {
    if (!data) return null
    const t = p => (p ? valueOf(metric, p.grossSales, p.orders) : null)
    return { cur: t(data.current), prev: t(data.previous) }
  }, [data, metric])

  const scale = niceScale([series.cur, series.prev], { money: m.money })
  const nothing = !data || data.current.orders === 0 || allZero(series.cur)
  const dots = days.length <= 45

  const chartData = {
    labels: days.map(d => dayShort(d.date)),
    datasets: kind === 'bar'
      ? [currentBars(series.cur), ...(hasPrev ? [comparisonBars(series.prev)] : [])]
      : [currentLine(series.cur, { fill: true, dots }), ...(hasPrev ? [comparisonLine(series.prev)] : [])]
  }

  const options = {
    ...baseOptions,
    scales: axes({ money: m.money, maxXTicks: 8, scale }),
    plugins: {
      ...baseOptions.plugins,
      tooltip: htmlTooltip(i => {
        const d = days[i]
        if (!d) return null
        const cur = series.cur[i]
        const prev = hasPrev ? series.prev[i] : null
        return {
          title: `${dayWithWeekday(d.date)}${d.closed ? ' · Closed' : ''}`,
          rows: [
            { color: C.brand, label: 'This period', value: d.closed ? 'Closed' : fmt(cur), square: kind === 'bar' },
            ...(hasPrev ? [{ color: kind === 'bar' ? C.prevBar : C.prev, dashed: kind !== 'bar', square: kind === 'bar', label: d.prevDate ? dayShort(d.prevDate) : 'Comparison', value: fmt(prev) }] : [])
          ],
          delta: hasPrev && !d.closed ? tooltipDelta(cur, prev, fmt) : null,
          note: d.closed ? 'The store was closed this day.' : null
        }
      })
    }
  }

  const Chart = kind === 'bar' ? Bar : Line
  const ariaSummary = data
    ? `${m.label} by day, ${rangeLabel(data.current.from, data.current.to)}. Total ${fmt(totals?.cur)}${hasPrev ? `, comparison ${fmt(totals?.prev)}` : ''}.`
    : 'Loading chart'

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography component="h2" sx={cardTitleSx}>Sales over time</Typography>
          <Typography sx={cardSubSx}>
            {data ? `${rangeLabel(data.current.from, data.current.to)}${hasPrev ? ` ${compareText.replace(/^vs/, 'vs.')}` : ''}` : ' '}
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={kind}
          onChange={(_, v) => v && setKind(v)}
          aria-label="Chart type"
          sx={{ '& .MuiToggleButton-root': { borderColor: C.border, color: C.textMuted, px: 1, '&.Mui-selected': { color: C.text, bgcolor: '#F3F4F6' } } }}>
          <ToggleButton value="line" aria-label="Line chart"><ShowChartIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="bar" aria-label="Bar chart"><BarChartIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Tabs value={metric} onChange={(_, v) => setMetric(v)} sx={{ ...tabsSx, mt: 1.5, borderBottom: `1px solid ${C.border}` }} aria-label="Metric">
        {METRICS.map(x => <Tab key={x.key} value={x.key} label={x.label} />)}
      </Tabs>

      {hasPrev || data ? (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }} aria-hidden>
          <LegendItem color={C.brand} label="This period" square={kind === 'bar'} />
          {hasPrev ? <LegendItem color={kind === 'bar' ? C.prevBar : C.prev} dashed={kind !== 'bar'} square={kind === 'bar'} label="Comparison" /> : null}
        </Box>
      ) : null}

      <Box sx={{ height: 300, mt: 1.5, position: 'relative' }} role="img" aria-label={ariaSummary}>
        {loading && !data ? (
          <Skeleton variant="rounded" height="100%" sx={{ borderRadius: '8px' }} />
        ) : nothing ? (
          <Empty onTry30={onTry30} message={data && data.current.orders > 0 ? `No ${m.label.toLowerCase()} to show in this range.` : 'No orders in this range.'} />
        ) : (
          <Chart data={chartData} options={options} />
        )}
      </Box>

      {/* One row per period: dot, name, dates, total. */}
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mt: 2.5, fontSize: 14, ...tabular }}>
        <Box component="thead">
          <Box component="tr" sx={{ borderBottom: `1px solid ${C.border}` }}>
            <Box component="th" sx={{ textAlign: 'left', fontWeight: 500, color: C.textMuted, fontSize: 12, py: 1 }}>Period</Box>
            <Box component="th" sx={{ textAlign: 'right', fontWeight: 500, color: C.textMuted, fontSize: 12, py: 1 }}>{m.label}</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {[
            { dot: C.brand, name: 'This period', p: data?.current, v: totals?.cur },
            ...(hasPrev ? [{ dot: C.prev, name: 'Comparison', p: data.previous, v: totals?.prev }] : [])
          ].map(row => (
            <Box component="tr" key={row.name} sx={{ borderBottom: `1px solid ${C.border}`, '&:last-child': { borderBottom: 0 } }}>
              <Box component="td" sx={{ py: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: row.dot, flexShrink: 0 }} />
                  <Box component="span" sx={{ fontWeight: 600, color: C.text }}>{row.name}</Box>
                  <Box component="span" sx={{ color: C.textMuted }}>{row.p ? rangeLabel(row.p.from, row.p.to) : ''}</Box>
                </Box>
              </Box>
              <Box component="td" sx={{ textAlign: 'right', py: 1.25, color: C.text, fontWeight: 500, whiteSpace: 'nowrap', pl: 1 }}>
                {loading && !data ? <Skeleton width={64} sx={{ ml: 'auto' }} /> : fmt(row.v)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export function LegendItem({ color, label, dashed, square }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {square
        ? <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color, boxShadow: 'inset 0 0 0 1px rgba(17,24,39,0.12)' }} />
        : <Box sx={{ width: 14, height: 0, borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}` }} />}
      <Typography sx={{ fontSize: 12, color: C.textMuted }}>{label}</Typography>
    </Box>
  )
}
